/**
 * Profiles - Módulo para la gestión de perfiles de usuario
 */
import $ from 'jquery';
import 'datatables.net-bs5';
import 'datatables.net-responsive-bs5';
import { escapeHtmlAttribute, getSpanishLanguage, } from '@/helpers';
export class Profiles {
    constructor() {
        this.TABLE_ID = '#profilesTable';
        this.dataTable = null;
        this.deactivateModal = null;
        this.activateModal = null;
        this.baseUrl = window.location.origin;
        this.init();
    }
    init() {
        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', () => {
                this.initializeModule();
            });
        }
        else {
            this.initializeModule();
        }
    }
    initializeModule() {
        this.initializeTable();
        this.initializeModals();
        this.setupEventListeners();
    }
    setupEventListeners() {
        // Escuchar cambios de tema para ajustar la tabla si es necesario
        window.addEventListener('theme-changed', () => {
            this.adjustTableForTheme();
        });
        // Manejar clicks en botones de acción (delegación de eventos)
        document.addEventListener('click', (e) => {
            const target = e.target;
            const deactivateBtn = target.closest('[data-action="deactivate"]');
            const activateBtn = target.closest('[data-action="activate"]');
            if (deactivateBtn) {
                e.preventDefault();
                this.handleDeactivate(deactivateBtn);
            }
            if (activateBtn) {
                e.preventDefault();
                this.handleActivate(activateBtn);
            }
        });
    }
    adjustTableForTheme() {
        // Ajustes visuales cuando cambia el tema (si es necesario)
        if (this.dataTable) {
            this.dataTable.draw(false);
        }
    }
    initializeModals() {
        const deactivateModalEl = document.getElementById('deactivateModal');
        const activateModalEl = document.getElementById('activateModal');
        if (deactivateModalEl) {
            this.deactivateModal = new window.bootstrap.Modal(deactivateModalEl);
        }
        if (activateModalEl) {
            this.activateModal = new window.bootstrap.Modal(activateModalEl);
        }
    }
    handleDeactivate(button) {
        const id = button.getAttribute('data-id');
        const name = button.getAttribute('data-name');
        if (!id || !name) {
            console.error('Missing id or name attribute');
            return;
        }
        const profileNameEl = document.getElementById('profileName');
        const confirmBtn = document.getElementById('confirmDeactivateBtn');
        if (profileNameEl) {
            profileNameEl.textContent = name;
        }
        if (confirmBtn) {
            confirmBtn.setAttribute('href', `${this.baseUrl}/profiles/change-status/${id}`);
        }
        if (this.deactivateModal) {
            this.deactivateModal.show();
        }
    }
    handleActivate(button) {
        const id = button.getAttribute('data-id');
        const name = button.getAttribute('data-name');
        if (!id || !name) {
            console.error('Missing id or name attribute');
            return;
        }
        const profileNameEl = document.getElementById('activateProfileName');
        const confirmBtn = document.getElementById('confirmActivateBtn');
        if (profileNameEl) {
            profileNameEl.textContent = name;
        }
        if (confirmBtn) {
            confirmBtn.setAttribute('href', `${this.baseUrl}/profiles/change-status/${id}`);
        }
        if (this.activateModal) {
            this.activateModal.show();
        }
    }
    initializeTable() {
        const tableElement = document.querySelector(this.TABLE_ID);
        if (!tableElement) {
            console.warn('Profiles table element not found');
            return;
        }
        try {
            this.dataTable = $(this.TABLE_ID).DataTable(this.getTableConfig());
        }
        catch (error) {
            console.error('Error initializing DataTables:', error);
        }
    }
    getTableConfig() {
        const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');
        return {
            processing: true,
            serverSide: true,
            responsive: false,
            language: getSpanishLanguage(),
            ajax: {
                url: `${this.baseUrl}/profiles/get-list`,
                type: 'POST',
                data: {
                    'csrf_token': csrfToken || ''
                },
                error: (xhr, error, thrown) => {
                    console.error('Error al cargar los datos:', error);
                    const tbody = document.querySelector(`${this.TABLE_ID} tbody`);
                    if (tbody) {
                        tbody.innerHTML = '<tr><td colspan="5" class="text-center text-danger">Error al cargar los datos. Por favor, recargue la página.</td></tr>';
                    }
                }
            },
            columns: [
                {
                    data: 'name_rol',
                    name: 'name_rol',
                    className: 'text-nowrap',
                },
                {
                    data: 'description_rol',
                    name: 'description_rol',
                    className: 'text-nowrap',
                },
                {
                    data: 'status_rol',
                    name: 'status_rol',
                    className: 'text-nowrap',
                    render: (data) => this.renderEstatus(data)
                },
                {
                    data: 'status_rol',
                    name: 'status_rol',
                    className: 'text-center text-nowrap',
                    orderable: false,
                    render: (data, type, row) => this.renderAcciones(row)
                }
            ],
            pageLength: 25,
            lengthMenu: [[10, 25, 50, 100], [10, 25, 50, 100]]
        };
    }
    renderEstatus(data) {
        const status = parseInt(data);
        if (status === 1) {
            return '<span class="badge bg-success"><i class="bi bi-check-circle me-1"></i>Activo</span>';
        }
        else {
            return '<span class="badge bg-danger"><i class="bi bi-x-circle me-1"></i>Inactivo</span>';
        }
    }
    renderAcciones(row) {
        let { name_rol: name, id_rol: id, status_rol: status, can_edit, can_change_status } = row;
        status = parseInt(status);
        let buttons = `
            <div class="btn-group role="group">
                ${can_edit ? (`
                        <a href="${this.baseUrl}/profiles/edit/${id}"
                           class="btn btn-sm btn-outline-primary"
                           title="Editar">
                            <i class="bi bi-pencil"></i>
                        </a>`) : ''}
        `;
        if (can_change_status) {
            if (status === 1) {
                buttons += `
                    <button type="button"
                            class="btn btn-sm btn-outline-danger"
                            data-action="deactivate"
                            data-id="${id}"
                            data-name="${escapeHtmlAttribute(name)}"
                            title="Desactivar">
                        <i class="bi bi-dash-circle"></i>
                    </button>
                `;
            }
            else {
                buttons += `
                    <button type="button"
                            class="btn btn-sm btn-outline-success"
                            data-action="activate"
                            data-id="${id}"
                            data-name="${escapeHtmlAttribute(name)}"
                            title="Activar">
                        <i class="bi bi-check-circle"></i>
                    </button>
                `;
            }
        }
        buttons += '</div>';
        return buttons;
    }
    /**
     * Obtiene la instancia del DataTable
     */
    getTable() {
        return this.dataTable;
    }
    /**
     * Recarga los datos de la tabla
     * @param resetPaging Si es true, resetea la paginación a la primera página
     */
    reload(resetPaging = false) {
        if (this.dataTable) {
            this.dataTable.ajax.reload(null, resetPaging);
        }
    }
    /**
     * Obtiene los datos seleccionados en la tabla
     */
    getSelectedRows() {
        if (this.dataTable) {
            return this.dataTable.rows('.selected').data().toArray();
        }
        return [];
    }
    /**
     * Filtra la tabla por una columna específica
     * @param column Índice de la columna
     * @param value Valor a buscar
     */
    filterByColumn(column, value) {
        if (this.dataTable) {
            this.dataTable.column(column).search(value).draw();
        }
    }
    /**
     * Limpia todos los filtros de la tabla
     */
    clearFilters() {
        if (this.dataTable) {
            this.dataTable.search('').columns().search('').draw();
        }
    }
}
export default Profiles;
